import PageLoader from '@components/PageLoader'
import { ErrorNote } from '@components/Notice'
import { EmptyNote } from '@components/EmptyState'
import Segmented from '@components/Segmented'
import { useAdminFunnel } from '@hooks/useAdminFunnel'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import React from 'react'
import { AdminHeading } from '../AdminLayout.styles'
import { SubText, Table, TableWrap, Td, Th, Toolbar } from '../adminList.styles'
import { formatDateOnly } from '../adminFormat'
import { rangeLabel } from '../dateRange'
import DistributionBars from '../Onboarding/DistributionBars'
import { formatShare } from '../Onboarding/onboardingView'
import {
	CohortNote,
	Section,
	SectionSubtitle,
	SectionTitle,
	WarningStrip,
} from '../Onboarding/OnboardingPage.styles'
import {
	ACTION_LABELS,
	deviceDataCoverage,
	excludedNotes,
	funnelRows,
	PLATFORM_OPTIONS,
	splitWidths,
	stepListPath,
	versionLabel,
} from './funnelView'
import {
	ExcludedList,
	KeySwatch,
	SplitKey,
	StepCount,
	StepCountLink,
	StepDetail,
	StepFill,
	StepLabel,
	StepRow,
	StepTrack,
	StepValue,
} from './FunnelPage.styles'
import { usePlatformParam } from './usePlatformParam'

/**
 * Admin activation funnel: of the devices first seen in the global date range,
 * how many signed up, got a trip and created planning content, each within the
 * funnel's window of that first open (ADR 0010).
 *
 * Step 1 counts devices and the later steps count people, each once. The trip
 * step is split by how each person got their first trip, and the version table
 * repeats the funnel per release so a change in onboarding shows up against the
 * version that shipped it. Each step count opens the list of who it counts; the
 * platform lives in the URL so coming back from that list keeps it.
 */
const FunnelPage: React.FC = () => {
	const { from, to } = useAdminFilter()
	const [platform, setPlatform] = usePlatformParam()
	const { data, isLoading, isError } = useAdminFunnel(platform)

	const toolbar = (
		<Toolbar>
			<Segmented
				aria-label="Platform"
				options={PLATFORM_OPTIONS}
				value={platform}
				onChange={setPlatform}
			/>
		</Toolbar>
	)

	if (isLoading) {
		return (
			<>
				<AdminHeading>Funnel</AdminHeading>
				{toolbar}
				<PageLoader label="Loading funnel…" />
			</>
		)
	}

	if (isError || !data) {
		return (
			<>
				<AdminHeading>Funnel</AdminHeading>
				{toolbar}
				<ErrorNote>Could not load the funnel. Please try again.</ErrorNote>
			</>
		)
	}

	const { steps, hasTripSplit, windowDays } = data
	const coverage = deviceDataCoverage(from, to, data.deviceDataStart)
	const dataStartLabel = formatDateOnly(data.deviceDataStart)

	if (coverage === 'none') {
		return (
			<>
				<AdminHeading>Funnel</AdminHeading>
				{toolbar}
				<EmptyNote>
					No data: devices were first recorded on {dataStartLabel}. Pick a range that ends on or
					after it.
				</EmptyNote>
			</>
		)
	}

	const rows = funnelRows(steps)
	const split = splitWidths(hasTripSplit, steps.newDevices)
	const notes = excludedNotes(data.excluded, windowDays)
	const showPlatform = data.platform === 'all'

	return (
		<>
			<AdminHeading>Funnel</AdminHeading>
			{toolbar}

			{coverage === 'partial' && (
				<WarningStrip role="status">
					<span aria-hidden="true">⚠</span>
					<span>
						No data before {dataStartLabel}, when devices were first recorded: this funnel only
						counts the days from then on.
					</span>
				</WarningStrip>
			)}

			<CohortNote>
				Devices first seen {rangeLabel(from, to)} · every step within {windowDays} days of the first
				open
			</CohortNote>

			<Section>
				<SectionTitle>Funnel</SectionTitle>
				<SectionSubtitle>
					New devices count devices; every step after counts people, each once. Percentages are of
					the step before.
				</SectionSubtitle>

				{rows.map(row => (
					<React.Fragment key={row.key}>
						<StepRow>
							<StepLabel>{row.label}</StepLabel>
							<StepTrack>
								{row.key === 'hasTrip' ? (
									<>
										<StepFill $width={split.created} />
										<StepFill $joined $width={split.joined} />
									</>
								) : (
									<StepFill $width={row.width} />
								)}
							</StepTrack>
							<StepValue>
								{row.count > 0 ? (
									<StepCountLink
										to={stepListPath(row.key, platform)}
										aria-label={`${row.label}: see all ${row.count.toLocaleString()}`}
									>
										{row.count.toLocaleString()}
									</StepCountLink>
								) : (
									<StepCount>0</StepCount>
								)}
								{row.conversion !== null && row.dropOff !== null && (
									<StepDetail>
										{row.conversion} · {row.dropOff.toLocaleString()} dropped off
									</StepDetail>
								)}
							</StepValue>
						</StepRow>
						{row.key === 'hasTrip' && (
							<StepRow>
								<span />
								<SplitKey>
									<span>
										<KeySwatch />
										{hasTripSplit.created.toLocaleString()} created a trip
									</span>
									<span>
										<KeySwatch $joined />
										{hasTripSplit.joined.toLocaleString()} joined one
									</span>
								</SplitKey>
								<span />
							</StepRow>
						)}
					</React.Fragment>
				))}

				{notes.length > 0 && (
					<ExcludedList aria-label="Left out of the funnel">
						{notes.map(note => (
							<li key={note}>Left out: {note}</li>
						))}
					</ExcludedList>
				)}
			</Section>

			<DistributionBars
				title="First planning action"
				subtitle={`What each of the ${steps.activated.toLocaleString()} activated people created first`}
				distribution={{
					answered: steps.activated,
					options: data.firstActions
						.filter(action => action.count > 0)
						.map(action => ({
							option: ACTION_LABELS[action.type],
							count: action.count,
							isUnknown: false,
						})),
				}}
			/>

			<Section>
				<SectionTitle>By app version</SectionTitle>
				<SectionSubtitle>
					The version each device first opened with, newest first. People count under the device
					they were first seen on.
				</SectionSubtitle>
				<TableWrap>
					<Table>
						<thead>
							<tr>
								{showPlatform && <Th>Platform</Th>}
								<Th>Version</Th>
								<Th>New devices</Th>
								<Th>Signed up</Th>
								<Th>Has a trip</Th>
								<Th>Activated</Th>
							</tr>
						</thead>
						<tbody>
							{data.versions.length === 0 ? (
								<tr>
									<Td colSpan={showPlatform ? 6 : 5}>
										<EmptyNote>No new devices in this range.</EmptyNote>
									</Td>
								</tr>
							) : (
								data.versions.map(row => (
									<tr key={`${row.platform}:${row.version}`}>
										{showPlatform && <Td>{row.platform}</Td>}
										<Td>{versionLabel(row.platform, row.version)}</Td>
										<Td>{row.newDevices.toLocaleString()}</Td>
										<Td>
											{row.signedUp.toLocaleString()}
											<SubText>{formatShare(row.signedUp, row.newDevices)}</SubText>
										</Td>
										<Td>
											{row.hasTrip.toLocaleString()}
											<SubText>{formatShare(row.hasTrip, row.signedUp)}</SubText>
										</Td>
										<Td>
											{row.activated.toLocaleString()}
											<SubText>{formatShare(row.activated, row.hasTrip)}</SubText>
										</Td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</TableWrap>
			</Section>
		</>
	)
}

export default FunnelPage
